import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { Card, CardContent } from '../ui/card';
import {
  Loader2,
  Calculator,
  Package,
  Users,
  Zap,
  TrendingUp,
  Save,
  Info
} from 'lucide-react';
import costEstimationService from '../../services/costEstimationService';

const CostEstimationDialog = ({ open, onClose, product }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const calculateCost = useCallback(async () => {
    if (!product) return;

    setLoading(true);
    setError(null);
    setResult(null);
    setSaved(false);

    try {
      const data = await costEstimationService.calculateCost({
        product_id: product._id,
        save_history: false
      });

      if (data.success) {
        setResult(data);
      } else {
        setError(data.error || '计算失败');
      }
    } catch (err) {
      setError(err.response?.data?.error || '成本估算失败');
    } finally {
      setLoading(false);
    }
  }, [product]);

  useEffect(() => {
    if (open && product) {
      calculateCost();
    }
  }, [open, product, calculateCost]);

  const handleClose = () => {
    setResult(null);
    setError(null);
    setSaved(false);
    onClose();
  };

  const handleSaveHistory = async () => {
    if (!product) return;

    setSaving(true);
    try {
      await costEstimationService.calculateCost({
        product_id: product._id,
        save_history: true
      });
      setSaved(true);
    } catch (err) {
      setError(err.response?.data?.error || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return `¥${parseFloat(value).toFixed(2)}`;
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            产品成本估算
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Product Info */}
          {product && (
            <Card className="bg-muted/50">
              <CardContent className="pt-4">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">产品名称:</span>
                    <span className="ml-2 font-medium">{product.name || '-'}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">分类:</span>
                    <span className="ml-2">{product.category || '-'}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">刃径:</span>
                    <span className="ml-2">{product.tip_diameter ? `${product.tip_diameter}mm` : '-'}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">总长:</span>
                    <span className="ml-2">{product.total_length ? `${product.total_length}mm` : '-'}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
              <span className="ml-2">计算中...</span>
            </div>
          ) : result && result.success ? (
            <>
              {/* Cost Breakdown */}
              <div className="grid grid-cols-3 gap-3">
                {/* Bar Stock Cost */}
                <Card>
                  <CardContent className="pt-4 text-center">
                    <Package className="h-6 w-6 mx-auto mb-2 text-blue-500" />
                    <div className="text-lg font-bold text-blue-600">
                      {formatCurrency(result.bar_stock?.unit_cost)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">棒料成本</div>
                    <div className="text-xs mt-2 border-t pt-2">
                      <div>{result.bar_stock?.specification}</div>
                      <div>{formatCurrency(result.bar_stock?.bar_price)}/根</div>
                      <div>可做 {result.bar_stock?.pieces_per_bar} 根</div>
                    </div>
                  </CardContent>
                </Card>

                {/* Labor Cost */}
                <Card>
                  <CardContent className="pt-4 text-center">
                    <Users className="h-6 w-6 mx-auto mb-2 text-green-500" />
                    <div className="text-lg font-bold text-green-600">
                      {formatCurrency(result.labor?.total)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">人工成本</div>
                    <div className="text-xs mt-2 border-t pt-2">
                      <div>{result.labor?.hours}小时</div>
                      <div>{formatCurrency(result.labor?.hourly_rate)}/时</div>
                    </div>
                  </CardContent>
                </Card>

                {/* Electricity Cost */}
                <Card>
                  <CardContent className="pt-4 text-center">
                    <Zap className="h-6 w-6 mx-auto mb-2 text-yellow-500" />
                    <div className="text-lg font-bold text-yellow-600">
                      {formatCurrency(result.electricity?.total)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">电费成本</div>
                    <div className="text-xs mt-2 border-t pt-2">
                      <div>{result.electricity?.hours}小时</div>
                      <div>{result.electricity?.power_kw}kW × {formatCurrency(result.electricity?.rate_per_kwh)}</div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Total Cost */}
              <Card className="bg-primary/5 border-primary/20">
                <CardContent className="py-4">
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-muted-foreground">总成本:</span>
                    <span className="text-2xl font-bold text-primary">
                      {formatCurrency(result.total_cost)}
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Margin Prices */}
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="h-4 w-4" />
                    <span className="font-medium">建议售价</span>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {Object.entries(result.margin_prices || {}).map(([margin, price]) => (
                      <div key={margin} className="text-center p-2 bg-muted rounded">
                        <div className="text-xs text-muted-foreground">毛利{margin}</div>
                        <div className="font-semibold">{formatCurrency(price)}</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Bar Stock Info */}
              {result.bar_stock && (
                <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                  <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div>
                    刃径 {result.bar_stock.original_diameter}mm → 匹配棒料 {result.bar_stock.matched_diameter}mm
                    {result.bar_stock.supplier_name && ` | 供应商: ${result.bar_stock.supplier_name}`}
                    {result.bar_stock.brand && ` | 牌号: ${result.bar_stock.brand}`}
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>

        <DialogFooter className="mt-4">
          {result && result.success && (
            <Button
              variant="outline"
              onClick={handleSaveHistory}
              disabled={saving || saved}
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : saved ? (
                '已保存'
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  保存记录
                </>
              )}
            </Button>
          )}
          <Button variant="secondary" onClick={handleClose}>
            关闭
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CostEstimationDialog;
